import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Page } from "@/components/Layout";
import { api } from "@/lib/api";
import { useCurrency } from "@/lib/currency";
import CurrencySwitcher from "@/components/CurrencySwitcher";
import { toast } from "sonner";
import { Calendar, Clock, CheckCircle, ClipboardText, UploadSimple, CreditCard, ArrowRight, X, CaretRight } from "@phosphor-icons/react";
import { openRazorpayCheckout } from "@/lib/razorpay";
import PushOptInModal from "@/components/PushOptInModal";
import { useAuth } from "@/lib/auth";
import { useAuthModal } from "@/components/AuthModal";
import PageLoader from "@/components/PageLoader";

const STEPS = [
  { id: "slot", label: "Slot", icon: Calendar },
  { id: "intake", label: "Intake", icon: ClipboardText },
  { id: "documents", label: "Documents", icon: UploadSimple },
  { id: "payment", label: "Payment", icon: CreditCard },
];

const MENTOR_SESSION_TYPES = [
  "Semester Wellbeing Session",
  "Career Strategy & Development",
  "Application Construction",
  "Application Review (CV / Supporting Statement)",
  "Mock Interview",
  "Cultural Adaptation & Integration",
  "Supporting Your Child's Journey Abroad",
];

export default function BookSession() {
  const { type = "therapist", id } = useParams();
  const navigate = useNavigate();
  const { format } = useCurrency();
  const { user, loading: authLoading } = useAuth();
  const { open: openAuth } = useAuthModal();

  const [step, setStep] = useState(0);
  const [provider, setProvider] = useState(null);
  const [intakeQs, setIntakeQs] = useState([]);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [form, setForm] = useState({ client_name: "", client_email: "", client_phone: "", session_type: "First Session" });
  const [intakeAns, setIntakeAns] = useState({});
  const [files, setFiles] = useState([]);
  const [booking, setBooking] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [bookedSlots, setBookedSlots] = useState({});

  // Logged-in users never retype their details
  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        client_name: f.client_name || user.name || "",
        client_email: f.client_email || user.email || "",
        client_phone: f.client_phone || user.phone || "",
      }));
    }
  }, [user]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const endpoint = type === "mentor" ? `/mentors/${id}` : `/therapists/${id}`;
    api.get(endpoint).then((r) => setProvider(r.data)).catch(() => toast.error("Could not load provider"));
    api.get(`/providers/${type === "mentor" ? "mentor" : "therapist"}/${id}/booked-slots`).then((r) => setBookedSlots(r.data || {})).catch(() => {});
    const division = type === "mentor" ? "forward" : "couch";
    api.get("/intake-questions", { params: { division } }).then((r) => setIntakeQs(r.data || [])).catch(() => {});
  }, [type, id]);

  const price = provider?.session_fee || 0;

  // Only the professional's own published availability is offered — no
  // hardcoded slots. Past dates and today's already-passed times are hidden.
  const availEntries = useMemo(() => {
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    const nowHM = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
    return (provider?.availability || [])
      .filter((a) => a && a.date && a.date >= today)
      .map((a) => ({
        ...a,
        times: (a.times || [])
          .filter((t) => (a.date > today ? true : t > nowHM))
          .filter((t) => !(bookedSlots[a.date] || []).includes(t)),
      }))
      .filter((a) => a.times.length > 0)
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [provider, bookedSlots]);
  const dateOptions = availEntries.map((a) => ({ ds: a.date, d: new Date(`${a.date}T00:00:00`) }));
  const timeOptions = availEntries.find((a) => a.date === date)?.times || [];
  // Derived from the same filtered entries, so it never points at a booked/past slot.
  const nextAvailable = availEntries.length
    ? `${new Date(`${availEntries[0].date}T00:00:00`).toLocaleDateString("en-US", { weekday: "short", day: "2-digit", month: "short" })}, ${availEntries[0].times[0]}`
    : null;

  const canAdvance = useMemo(() => {
    if (step === 0) return Boolean(date && time && form.client_name && form.client_email && form.client_phone.trim().length >= 7 && (type !== "mentor" || form.session_type));
    if (step === 1) {
      const required = intakeQs.filter((q) => q.required);
      return required.every((q) => {
        const v = intakeAns[q.id];
        if (Array.isArray(v)) return v.length > 0;
        return v !== undefined && String(v).trim() !== "";
      });
    }
    return true;
  }, [step, date, time, form, intakeQs, intakeAns]);

  const goNext = () => {
    if (!canAdvance) {
      toast.error("Please complete the required fields");
      return;
    }
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };
  const goPrev = () => setStep((s) => Math.max(0, s - 1));

  const createBooking = async () => {
    setBusy(true);
    try {
      const serviceSlug = type === "therapist" ? sessionStorage.getItem("md_service_slug") : null;
      const r = await api.post("/bookings", {
        provider_id: id,
        provider_type: type,
        session_type: form.session_type,
        date,
        time_slot: time,
        ...form,
        amount: price,
        ...(serviceSlug ? { service_slug: serviceSlug } : {}),
        intake_responses: intakeAns,
        document_names: files.map((f) => f.name),
      });
      sessionStorage.removeItem("md_service_slug");
      setBooking(r.data);
      return r.data;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create booking");
      if (e.response?.status === 409) {
        // Slot got taken — refresh taken-slot map so the picker updates.
        api.get(`/providers/${type === "mentor" ? "mentor" : "therapist"}/${id}/booked-slots`).then((r) => setBookedSlots(r.data || {})).catch(() => {});
        setTime("");
      }
      return null;
    } finally {
      setBusy(false);
    }
  };

  const handlePay = async () => {
    const b = booking || (await createBooking());
    if (!b) return;
    await openRazorpayCheckout({
      orderType: "booking",
      orderId: b.id,
      amount: price,
      name: form.client_name,
      email: form.client_email,
      description: `${provider.name} · ${form.session_type} · ${date} ${time}`,
      onSuccess: () => setConfirmed(true),
    });
  };

  if (!provider) return <Page><PageLoader /></Page>;

  if (!authLoading && !user) {
    return (
      <Page>
        <section className="md-section">
          <div className="md-container max-w-2xl text-center" data-testid="booking-login-gate">
            <h1 className="text-4xl font-serif mb-3">Please log in to book a session</h1>
            <p className="text-base mb-8" style={{ color: "var(--md-text-soft)" }}>
              Booking takes less than a minute once you&apos;re signed in — your details are filled in automatically.
            </p>
            <button onClick={() => openAuth("login")} className="btn-primary" data-testid="booking-login-btn">
              Log in / Sign up <ArrowRight size={14} weight="bold" />
            </button>
          </div>
        </section>
      </Page>
    );
  }

  if (confirmed) {
    return (
      <Page>
        <section className="md-section">
          <div className="md-container max-w-2xl text-center">
            <CheckCircle size={72} weight="duotone" style={{ color: "var(--md-primary)" }} className="mx-auto mb-5" />
            <h1 className="text-4xl md:text-5xl font-serif mb-3">You&apos;re on the calendar</h1>
            <p className="text-base mb-8" style={{ color: "var(--md-text-soft)" }}>
              A confirmation has been queued for <strong>{form.client_email}</strong>. {provider.name} will reach out with the Google Meet link 24h before the session.
            </p>
            <div className="md-card text-left">
              <div className="text-sm" style={{ color: "var(--md-text-muted)" }}>Session</div>
              <div className="text-xl font-serif">{provider.name} · {form.session_type}</div>
              <div className="mt-2 text-sm">{new Date(date).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })} at {time}</div>
              <div className="mt-2 text-sm" style={{ color: "var(--md-text-soft)" }}>Booking ID: {booking?.id?.slice(0, 8)}</div>
            </div>
            <Link to="/dashboard" className="btn-primary mt-8" data-testid="booking-success-dashboard">Go to my dashboard <ArrowRight size={14} weight="bold" /></Link>
          </div>
        </section>
        <PushOptInModal bookingId={booking?.id} />
      </Page>
    );
  }

  return (
    <Page>
      <section className="md-section">
        <div className="md-container max-w-5xl">
          {/* Provider summary header */}
          <div className="book-header">
            <div>
              <div className="md-label-eyebrow mb-2">Book with</div>
              <h1 className="text-3xl md:text-4xl font-serif">{provider.name}</h1>
              <p className="text-sm mt-1" style={{ color: "var(--md-text-soft)" }}>{provider.title}</p>
              {nextAvailable && (
                <div className="mt-3 inline-flex items-center gap-2 text-sm font-medium px-3 py-1.5 rounded-full" style={{ background: "var(--md-bg-alt)", color: "var(--md-primary)" }} data-testid="book-next-available">
                  <Clock size={14} weight="duotone" /> Next available: {nextAvailable}
                </div>
              )}
            </div>
            <div className="book-price-card">
              <div className="text-xs uppercase tracking-wider" style={{ color: "var(--md-text-muted)" }}>From</div>
              <div className="book-price">{format(price)}</div>
              <div className="text-xs" style={{ color: "var(--md-text-muted)" }}>per 60-min session</div>
              <div className="mt-3"><CurrencySwitcher compact /></div>
            </div>
          </div>

          {/* Stepper */}
          <ol className="book-stepper" data-testid="booking-stepper">
            {STEPS.map((s, i) => (
              <li key={s.id} className={`book-step ${i === step ? "active" : ""} ${i < step ? "done" : ""}`} data-testid={`step-${s.id}`}>
                <span className="book-step-bubble"><s.icon size={16} weight="bold" /></span>
                <span className="book-step-label">{s.label}</span>
                {i < STEPS.length - 1 && <CaretRight size={14} className="book-step-divider" />}
              </li>
            ))}
          </ol>

          {/* Step bodies */}
          {step === 0 && (
            <div className="md-card book-card" data-testid="step-slot-body">
              <h3 className="font-serif text-xl mb-4"><Calendar size={18} className="inline mr-2" weight="duotone" />Pick a date</h3>
              {availEntries.length === 0 ? (
                <p className="text-sm" style={{ color: "var(--md-text-muted)" }} data-testid="book-no-availability">
                  {provider.name} hasn&apos;t published any upcoming availability yet. Please check back soon or <Link to="/contact" className="underline">contact us</Link> to arrange a slot.
                </p>
              ) : (
              <div className="book-date-strip">
                {dateOptions.map(({ ds, d }) => (
                    <button key={ds} type="button" onClick={() => { setDate(ds); if (time && !(availEntries.find((a) => a.date === ds)?.times || []).includes(time)) setTime(""); }} className={`book-date-chip ${date === ds ? "active" : ""}`} data-testid={`book-date-${ds}`}>
                      <div className="text-[10px] uppercase tracking-wider opacity-70">{d.toLocaleDateString("en-US", { weekday: "short" })}</div>
                      <div className="text-xl font-serif">{d.getDate()}</div>
                      <div className="text-[10px] opacity-70">{d.toLocaleDateString("en-US", { month: "short" })}</div>
                    </button>
                ))}
              </div>
              )}

              <h3 className="font-serif text-xl mt-6 mb-4"><Clock size={18} className="inline mr-2" weight="duotone" />Pick a time</h3>
              <div className="book-time-grid">
                {!date ? (
                  <p className="text-sm" style={{ color: "var(--md-text-muted)" }} data-testid="book-pick-date-hint">Pick a date above to see this professional&apos;s available times.</p>
                ) : (
                  timeOptions.map((t) => (
                    <button key={t} type="button" onClick={() => setTime(t)} className={`book-time-chip ${time === t ? "active" : ""}`} data-testid={`book-time-${t}`}>{t}</button>
                  ))
                )}
              </div>

              <div className={`grid gap-3 mt-7 ${type === "mentor" ? "md:grid-cols-2" : "md:grid-cols-3"}`}>
                <div>
                  <label className="md-label-eyebrow block mb-1.5">Your name</label>
                  <input required className="md-input" value={form.client_name} readOnly data-testid="book-client-name" />
                </div>
                <div>
                  <label className="md-label-eyebrow block mb-1.5">Email</label>
                  <input required type="email" className="md-input" value={form.client_email} readOnly data-testid="book-client-email" />
                </div>
                <div>
                  <label className="md-label-eyebrow block mb-1.5">Phone *</label>
                  <input required type="tel" className="md-input" value={form.client_phone} onChange={(e) => setForm({ ...form, client_phone: e.target.value })} data-testid="book-client-phone" />
                </div>
                {type === "mentor" && (
                  <div>
                    <label className="md-label-eyebrow block mb-1.5">Session type *</label>
                    <select required className="md-input" value={form.session_type} onChange={(e) => setForm({ ...form, session_type: e.target.value })} data-testid="book-session-type">
                      <option value="">Choose session type…</option>
                      {MENTOR_SESSION_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="md-card book-card" data-testid="step-intake-body">
              <h3 className="font-serif text-xl mb-2">Pre-session intake</h3>
              <p className="text-sm mb-5" style={{ color: "var(--md-text-soft)" }}>A few thoughtful questions so {provider.name.split(" ")[0]} can hold space better from the very first minute.</p>
              {intakeQs.length === 0 && (
                <p className="text-sm" style={{ color: "var(--md-text-muted)" }}>No intake questions configured. You can continue.</p>
              )}
              <div className="space-y-5">
                {intakeQs.map((q) => (
                  <div key={q.id} data-testid={`intake-q-${q.id}`}>
                    <label className="md-label-eyebrow block mb-1.5">{q.question}{q.required && <span className="text-red-500"> *</span>}</label>
                    {q.field_type === "textarea" && (
                      <textarea className="md-input" rows={3} value={intakeAns[q.id] || ""} onChange={(e) => setIntakeAns({ ...intakeAns, [q.id]: e.target.value })} />
                    )}
                    {q.field_type === "text" && (
                      <input className="md-input" value={intakeAns[q.id] || ""} onChange={(e) => setIntakeAns({ ...intakeAns, [q.id]: e.target.value })} />
                    )}
                    {q.field_type === "select" && (
                      <select className="md-input" value={intakeAns[q.id] || ""} onChange={(e) => setIntakeAns({ ...intakeAns, [q.id]: e.target.value })}>
                        <option value="">Choose…</option>
                        {(q.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    )}
                    {q.field_type === "scale" && (
                      <div className="flex gap-2">
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                          <button type="button" key={n} onClick={() => setIntakeAns({ ...intakeAns, [q.id]: n })} className={`scale-btn ${intakeAns[q.id] === n ? "active" : ""}`}>{n}</button>
                        ))}
                      </div>
                    )}
                    {q.field_type === "multiselect" && (
                      <div className="flex flex-wrap gap-2">
                        {(q.options || []).map((o) => {
                          const sel = Array.isArray(intakeAns[q.id]) ? intakeAns[q.id] : [];
                          const active = sel.includes(o);
                          return (
                            <button type="button" key={o} onClick={() => {
                              const next = active ? sel.filter((x) => x !== o) : [...sel, o];
                              setIntakeAns({ ...intakeAns, [q.id]: next });
                            }} className={`md-chip ${active ? "active" : ""}`}>{o}</button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="md-card book-card" data-testid="step-documents-body">
              <h3 className="font-serif text-xl mb-2">Upload supporting documents (optional)</h3>
              <p className="text-sm mb-5" style={{ color: "var(--md-text-soft)" }}>
                Past reports, journaling notes, or anything you&apos;d like {provider.name.split(" ")[0]} to read before your first session.
              </p>
              <label className="upload-dropzone" data-testid="upload-dropzone">
                <UploadSimple size={32} weight="duotone" />
                <span className="font-medium mt-2">Click to upload</span>
                <span className="text-xs" style={{ color: "var(--md-text-muted)" }}>PDF, DOC or images up to 10MB each</span>
                <input type="file" multiple className="hidden" onChange={(e) => setFiles([...files, ...Array.from(e.target.files || [])])} data-testid="upload-input" />
              </label>
              {files.length > 0 && (
                <ul className="upload-files mt-4" data-testid="upload-files">
                  {files.map((f, i) => (
                    <li key={`${f.name}-${f.size}-${f.lastModified || i}`} className="upload-file" data-testid={`upload-file-${i}`}>
                      <span className="truncate">{f.name}</span>
                      <span className="text-xs" style={{ color: "var(--md-text-muted)" }}>{(f.size / 1024).toFixed(0)} KB</span>
                      <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} aria-label="Remove file"><X size={14} /></button>
                    </li>
                  ))}
                </ul>
              )}
              <p className="text-xs mt-4" style={{ color: "var(--md-text-muted)" }}>Note: Document upload UI complete. Server-side storage is mocked in this iteration, only file names are recorded. Full file storage rolls out with Razorpay keys.</p>
            </div>
          )}

          {step === 3 && (
            <div className="md-card book-card" data-testid="step-payment-body">
              <h3 className="font-serif text-xl mb-4">Review &amp; pay</h3>
              <ul className="space-y-2 text-sm mb-6">
                <li><strong>Provider:</strong> {provider.name} ({provider.title})</li>
                <li><strong>When:</strong> {new Date(date).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })} at {time}</li>
                <li><strong>Type:</strong> {form.session_type}</li>
                <li><strong>Intake:</strong> {Object.keys(intakeAns).length} answer{Object.keys(intakeAns).length === 1 ? "" : "s"}</li>
                <li><strong>Documents:</strong> {files.length} file{files.length === 1 ? "" : "s"}</li>
              </ul>
              <div className="book-pay-row">
                <div>
                  <div className="text-xs uppercase tracking-wider" style={{ color: "var(--md-text-muted)" }}>Total</div>
                  <div className="book-price">{format(price)}</div>
                </div>
                <button onClick={handlePay} disabled={busy} className="btn-primary" data-testid="booking-pay-btn">
                  {busy ? "Processing…" : (<>Pay {format(price)} <ArrowRight size={14} weight="bold" /></>)}
                </button>
              </div>
              <p className="text-xs mt-3 flex items-center gap-1.5" style={{ color: "var(--md-primary)" }} data-testid="book-hold-note">
                <Clock size={13} weight="duotone" /> Your slot is held for 10 minutes while you complete payment.
              </p>
              <p className="text-xs mt-4 p-3 rounded" style={{ background: "var(--md-bg-alt)", color: "var(--md-text-muted)" }}>
                <strong>Test mode:</strong> Razorpay live checkout activates once test keys are added in Admin → Settings. For now, clicking Pay simulates a successful charge so you can review the entire booking flow end-to-end.
              </p>
            </div>
          )}

          {/* Step nav */}
          <div className="book-step-nav">
            <button type="button" onClick={goPrev} disabled={step === 0} className="btn-outline" data-testid="book-step-back">Back</button>
            {step < STEPS.length - 1 ? (
              <button type="button" onClick={goNext} className="btn-primary" data-testid="book-step-next">
                Continue <ArrowRight size={14} weight="bold" />
              </button>
            ) : (
              <span />
            )}
          </div>
        </div>
      </section>
    </Page>
  );
}
